import classNames from 'classnames';
import React, {
  ChangeEvent,
  cloneElement,
  FocusEventHandler,
  forwardRef,
  Fragment,
  HTMLAttributes,
  KeyboardEventHandler,
  ReactElement,
} from 'react';

import styles from './SelectedItemToolbar.module.css';
import { InlineInput } from '../InlineInput';
import { Tooltip } from '../Tooltip';

export interface SelectedItemToolbarAction {
  /**
   * Icon element to be shown in the action button.
   */
  icon: ReactElement<{ className?: string }>;
  /**
   * The label text for the action,
   * used as an aria-label for the button as well as a tooltip text.
   */
  label: string;
  /**
   * The event handler for the action.
   */
  onClick?: () => void;
  /**
   * Whether or not to show the button in "pressed" state.
   * Having boolean will set the 'aria-pressed' attribute to 'true' or 'false'.
   */
  pressed?: boolean | null;
}

export interface SelectedItemToolbarActionSection {
  /**
   * Actions to be available in this section.
   */
  actions: SelectedItemToolbarAction[];
}

export interface SelectedItemToolbarMenuItem {
  /**
   * ID of the item.
   */
  id: string;
  /**
   * The label text for the menu item.
   */
  label: string;
}

export interface SelectedItemToolbarProps extends Pick<
  HTMLAttributes<HTMLDivElement>,
  'className' | 'style'
> {
  /**
   * Action sections to be shown in the toolbar, separated by a vertical divider.
   */
  actionSections: SelectedItemToolbarActionSection[];
  /**
   * Whether or not to show the logic indicator.
   * If set to non-empty value, the indicator is shown, using this text as 'aria-label'.
   */
  indicatorLabel?: string | null;
  /**
   * Tooltip text for the logic indicator.
   */
  indicatorTooltip?: string | null;
  /**
   * The label for the label-editing input field, for accessibility.
   */
  inputLabel: string;
  /**
   * The current label text to be shown for the toolbar.
   * This will also be the initial edited value when double-clicked.
   */
  label: string;
  /**
   * Optional tooltip text for the editable label.
   */
  labelTooltip?: string | null;
  /**
   * Maximum length of the label text.
   */
  maxLabelLength?: number;
  /**
   * The event handler when the value on the label editing input field changes.
   * This one takes the new value as the first parameter and the event as the second parameter.
   * It's easier to use than `onChange`.
   */
  onLabelChange?:
    | ((value: string, event: ChangeEvent<HTMLInputElement>) => void)
    | undefined;
  /**
   * Placeholder text to show in place of the label when the label is empty.
   */
  placeholder?: string;
}

export const SelectedItemToolbar = forwardRef<HTMLDivElement, SelectedItemToolbarProps>(
  (props, ref) => {
    const {
      actionSections,
      className,
      indicatorLabel,
      indicatorTooltip,
      inputLabel,
      label,
      labelTooltip,
      maxLabelLength,
      onLabelChange,
      placeholder,
      style,
    } = props;

    const onFocus: FocusEventHandler<HTMLInputElement> = (event) => {
      event.target.select();
    };

    const onLabelKeyDown: KeyboardEventHandler<HTMLInputElement> = (event) => {
      if (event.key === 'Enter' || event.key === 'Escape') {
        // Loose focus when pressing Enter or Escape
        event.preventDefault();

        event.currentTarget.blur();
      }
    };

    return (
      <div
        className={classNames(styles.toolbar, className)}
        ref={ref}
        style={style}
      >
        {indicatorLabel && (
          <Tooltip
            tooltipText={indicatorTooltip || null}
            visible="hover"
          >
            <div className={styles.indicator} />
          </Tooltip>
        )}
        <Tooltip
          tooltipText={labelTooltip || null}
          visible="hover"
        >
          <InlineInput
            className={classNames(styles.label, !label && styles['label-empty'])}
            inputClassName={styles['label-input']}
            label={inputLabel}
            maxLength={maxLabelLength}
            onFocus={onFocus}
            onKeyDown={onLabelKeyDown}
            onValueChange={onLabelChange}
            placeholder={placeholder}
            value={label}
          />
        </Tooltip>
        <div className={styles.actions}>
          {actionSections.map((section, sectionIndex) => (
            // eslint-disable-next-line react/no-array-index-key
            <Fragment key={sectionIndex}>
              {sectionIndex > 0 && (
                <div
                  aria-hidden
                  className={styles.divider}
                />
              )}
              {section.actions.map((action, actionIndex) => (
                <Tooltip
                  // eslint-disable-next-line react/no-array-index-key
                  key={actionIndex}
                  tooltipText={action.label}
                  visible="hover"
                >
                  <button
                    aria-label={action.label}
                    aria-pressed={action.pressed ?? undefined}
                    className={styles.action}
                    onClick={action.onClick}
                    type="button"
                  >
                    {cloneElement(action.icon, {
                      className: classNames(
                        action.icon.props.className,
                        styles['action-icon'],
                      ),
                    })}
                  </button>
                </Tooltip>
              ))}
            </Fragment>
          ))}
        </div>
      </div>
    );
  },
);
